import { Box, Button, Flex, Text, Tooltip } from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import { EmptyState, PageContainer, PageHeader, Skeleton } from 'components/ds';
import { dateFormat, relativeTimeFormat } from 'helpers/date';
import { getUserInfoStorage } from 'helpers/lib';
import { RefreshCw } from 'lucide-react';
import useSynchronizeHistory from 'queries/useSynchronizeHistory';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { SyncDocumentHistory } from 'services/synchronization/synchronization.type';

import SyncActionButtons from './SyncActionButtons';
import SyncStatusBadge from './SyncStatusBadge';
import SyncTable from './SyncTable';

// Initials from a name or email ("dat.nguyen@x.com" -> "DN", "Le Van" -> "LV").
const initialsOf = (s = ''): string => {
  const base = s.includes('@') ? s.split('@')[0] : s;
  const parts = base.split(/[.\s_-]+/).filter(Boolean);
  const ini = (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
  return (ini || base.slice(0, 2)).toUpperCase();
};

const Synchronization = () => {
  const { data = [], isFetching } = useSynchronizeHistory();
  const { t } = useTranslation();

  const isEmpty = !isFetching && data.length === 0;

  // BE only lets the owner delete: sync_by vs the token's preferred_username, case-insensitive.
  const user = getUserInfoStorage();
  const me = (user?.username || user?.email || '').toLowerCase();
  const isOwnSync = (row: SyncDocumentHistory) => !!me && (row.sync_by || '').toLowerCase() === me;

  const columns: ColumnDef<SyncDocumentHistory>[] = [
    {
      accessorKey: 'sync_by',
      header: t('synchronization.syncBy'),
      meta: { maxW: '200px' },
      // Triggered-by → circular initials avatar + name (mockup), derived from existing sync_by.
      cell: (info) => {
        const name = (info.getValue() as string) || '';
        return (
          <Flex align='center' gap={2.5} minW={0}>
            <Flex
              align='center'
              justify='center'
              flexShrink={0}
              boxSize='28px'
              color='primary.solid'
              fontSize='xs'
              fontWeight={600}
              bg='primary.soft'
              borderRadius='full'
            >
              {initialsOf(name)}
            </Flex>
            <Tooltip label={name} placement='top'>
              <Text as='span' isTruncated>
                {name}
              </Text>
            </Tooltip>
          </Flex>
        );
      },
    },
    {
      accessorKey: 'spo_connection_display_name',
      header: t('synchronization.connectionName'),
    },
    {
      accessorKey: 'spo_site',
      header: t('synchronization.site'),
      // Show the full site URL (don't truncate) — QA wants full site info. (#78323)
      meta: { noTruncate: true, minW: '200px' },
    },
    {
      accessorKey: 'state',
      header: t('synchronization.state'),
      cell: (info) => <SyncStatusBadge statusKey={info.getValue() as string} />,
    },
    {
      accessorKey: 'spo_folder',
      header: t('synchronization.folders', { defaultValue: 'Folders' }),
      // Show the folder DIRECTORY (path), matching production/mockup — not a count. (#78082-4)
      // Parent folder, then each selected sub-folder on its own line (path relative to the
      // parent) instead of a truncated one-liner + tooltip.
      meta: { noTruncate: true, minW: '220px' },
      cell: (info) => {
        const dir = (info.getValue() as string) || '';
        const subs = (info.row.original.sub_folders || [])
          .filter((sub) => sub && sub !== dir)
          .map((sub) => (dir && sub.startsWith(`${dir}/`) ? sub.slice(dir.length + 1) : sub));
        if (!dir && !subs.length) return '—';
        return (
          <Box sx={{ overflowWrap: 'anywhere' }} lineHeight='short'>
            {dir && <Text color='text.default'>{dir}</Text>}
            {subs.map((sub) => (
              <Text key={sub} mt={1} pl={3} color='text.muted' fontSize='xs'>
                ↳ {sub}
              </Text>
            ))}
          </Box>
        );
      },
    },
    {
      accessorKey: 'start_time',
      header: t('synchronization.when', { defaultValue: 'When' }),
      meta: { minW: '96px' },
      // When → relative timestamp (mockup) from existing start_time; full date on hover.
      cell: (info) => {
        const v = info.getValue() as string;
        return (
          <Tooltip label={v ? dateFormat(v) : ''} placement='top'>
            <Text as='span' color='text.muted'>
              {relativeTimeFormat(v)}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      meta: { minW: '56px' },
      // Unsync a single folder without deleting the whole connection. BE only lets the user who
      // triggered a sync delete it, so hide the action on other users' rows.
      cell: (info) =>
        isOwnSync(info.row.original) ? <SyncActionButtons info={info.row.original} /> : null,
    },
  ];

  return (
    <PageContainer>
      {/* Page header — mockup .page-header (title + subtitle + primary action) */}
      <PageHeader
        title={t('synchronization.title')}
        subtitle={t('synchronization.subtitle', {
          defaultValue: 'History of document syncs from connected SharePoint libraries.',
        })}
        action={
          <Link to={'/sync-projects'}>
            <Button leftIcon={<RefreshCw size={16} />} size='ds-md' variant='ds-primary'>
              {t('synchronization.createANewSync')}
            </Button>
          </Link>
        }
      />

      {/* Table wrapper — mockup .table-wrap (r-lg border surface).
          NOTE: SyncTable owns its own bordered, overflow-x:auto container so the
          table can scroll horizontally on narrow viewports. Skeleton/empty states
          keep a matching bordered box here. */}
      {isFetching ? (
        /* DS loading skeleton — mirrors table rows (design-guidelines.md §4) */
        <Box
          overflow='hidden'
          p={4}
          bg='bg.surface'
          borderWidth='1px'
          borderColor='border.default'
          borderRadius='lg'
        >
          <Skeleton h='14px' w='40%' mb={4} />
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} h='44px' borderRadius='md' mb={2} />
          ))}
        </Box>
      ) : isEmpty ? (
        /* DS empty state (design-guidelines.md §4) */
        <Box
          overflow='hidden'
          bg='bg.surface'
          borderWidth='1px'
          borderColor='border.default'
          borderRadius='lg'
        >
          <EmptyState
            icon={<RefreshCw size={26} />}
            title={t('common.noData')}
            description={t('synchronization.emptyDescription', {
              defaultValue: 'No synchronization instances yet. Create a new sync to get started.',
            })}
          />
        </Box>
      ) : (
        <SyncTable columns={columns} data={data} />
      )}
    </PageContainer>
  );
};

export default Synchronization;
